import React from "react";
import { useTranslation } from "react-i18next";
import TextArea from "../../../../../components/TextArea";
import Dropdown from "../../../../../components/Dropdown";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";

interface OtherDetailsSectionProps {
  notes: string;
  status: string;
  originalStatus: string;
  statusOptions: any[];
  isAdminCaller: boolean;
  onNotesChange: (e: any) => void;
  onStatusChange: (e: any) => void;
}

const OtherDetailsSection: React.FC<OtherDetailsSectionProps> = ({
  notes, status, originalStatus, statusOptions, isAdminCaller, onNotesChange, onStatusChange,
}) => {
  const { t } = useTranslation();
  const base = 'reconciliation.managerStatement.detail.pageTexts.detailsTab.otherDetails';

  // EVYA-1421: mirrors ManagerStatementService::isAllowedToSetStatus() exactly, so the UI
  // never offers a transition the server will reject. Paid is final for everyone; from
  // Completed, Admin may only stay Completed or move to Paid, Manager may only stay
  // Completed; otherwise Admin is unrestricted (except Paid is only reachable from
  // Completed) and Manager may only resubmit Ready for Review or leave status unchanged.
  const isTargetAllowed = (targetStatus: string): boolean => {
    if (originalStatus === 'paid') return false;

    if (originalStatus === 'completed') {
      return isAdminCaller
        ? targetStatus === 'completed' || targetStatus === 'paid'
        : targetStatus === 'completed';
    }

    if (targetStatus === 'paid') return false;
    if (isAdminCaller) return true;

    return targetStatus === originalStatus || targetStatus === 'ready_for_review';
  };

  const dropdownOptions = (statusOptions || []).map((option: any) => ({
    label: option.name,
    value: option.id,
    disabled: !isTargetAllowed(option.id),
  }));

  return (
    <div>
      <h6 className="mb-4">{t(`${base}.title`)}</h6>

      <div className="row g-6 mb-4">
        <div className="col-md-9">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.statementNotes}
              name={VALIDATION_FIELD_MAPPING.statementNotes}
              label={t(`${base}.notesLabel`)}
              placeholder={t(`${base}.notesPlaceholder`)}
              rows={4}
              className="form-control-lg h-100"
              value={notes}
              onChange={onNotesChange}
            />
          </div>
        </div>

        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={[status]}
              noSearch
              options={dropdownOptions}
              allOptions={dropdownOptions}
              id={VALIDATION_FIELD_MAPPING.statementStatus}
              name={VALIDATION_FIELD_MAPPING.statementStatus}
              label={t(`${base}.statusLabel`)}
              placeholder={t(`${base}.statusLabel`)}
              onChange={onStatusChange}
            />
          </div>
          <div className="form-text">{t(`${base}.statusHint.${status}`)}</div>
        </div>
      </div>
    </div>
  );
};

export default OtherDetailsSection;
